import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';
import {makeSizeConstants} from '../../../libs/shared/ui/src/styles/styleHelpers';
import {ThemeReader, useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export enum InlineBannerTypesEnum {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  TIP = 'TIP',
}

interface InlineBannerActionProps {
  title: ReactNode;
  onClick: Handler;
  id: string;
  iconName?: IconName;
}

interface InlineBannerProps {
  type: InlineBannerTypesEnum;
  title: ReactNode;
  isNewDesign?: boolean;
  hasSmallBorderRadius?: boolean;
  hasColorfulText?: boolean;
  hasInlineActions?: boolean;
  hasInlineActionsWithClose?: boolean;
  actions?: ReadonlyArray<InlineBannerActionProps>;
  size: VisualSizesEnum;
  imageUrl?: string;
  onClose?: Handler;
  className?: string;
  closeLabel?: ReactNode;
}

/*
 * Style.
 */

interface BannerConstants {
  findIconColor: ThemeReader;
  findBackgroundColor: ThemeReader;
  icon: IconName;
}

const bannerConstants: {[k in InlineBannerTypesEnum]: BannerConstants} = {
  [InlineBannerTypesEnum.INFO]: {
    findBackgroundColor: (theme) => theme.palette.blue.shade20,
    findIconColor: (theme) => theme.palette.blue.shade40,
    icon: 'infoFilledSmall',
  },
  [InlineBannerTypesEnum.SUCCESS]: {
    findBackgroundColor: (theme) => theme.palette.green.shade20,
    findIconColor: (theme) => theme.palette.green.shade40,
    icon: 'checkmarkCircleSmall',
  },
  [InlineBannerTypesEnum.ERROR]: {
    findBackgroundColor: (theme) => theme.palette.red.shade20,
    findIconColor: (theme) => theme.palette.red.shade40,
    icon: 'warningFilledSmall',
  },
  [InlineBannerTypesEnum.WARNING]: {
    findBackgroundColor: (theme) => theme.palette.orange.shade20,
    findIconColor: (theme) => theme.palette.orange.shade50,
    icon: 'warningFilledSmall',
  },
  [InlineBannerTypesEnum.TIP]: {
    findBackgroundColor: (theme) => theme.palette.pink.shade10,
    findIconColor: (theme) => theme.palette.pink.shade40,
    icon: 'lightbulbSmall',
  },
};

interface BannerInlineActionStyleProps {
  $hasInlineActionsWithClose?: boolean;
}

interface BannerIconStyleProps {
  $type: InlineBannerTypesEnum;
}
interface BannerInsideCardStyleProps {
  $hasSmallBorderRadius?: boolean;
  $hasColorfulText?: boolean;
  $isNewDesign?: boolean;
}
interface BannerSizeStyleProps {
  $size: VisualSizesEnum;
}
interface BannerStyleProps
  extends BannerIconStyleProps,
    BannerInsideCardStyleProps,
    BannerSizeStyleProps,
    BannerInlineActionStyleProps {
  $hasImage?: boolean;
  $hasContent?: boolean;
  $hasInlineActions?: boolean;
  $hasCloseInSmall?: boolean;
}

const bannerFontSizes = makeSizeConstants(fontSizes.verySmall, fontSizes.medium);
const bannerLineHeights = makeSizeConstants('18px', '20px');
const bannerBorderRadii = makeSizeConstants('8px', '16px');

const StyledBannerDiv = styled.div<BannerStyleProps>`
  ${(p) => css`
    background-color: ${bannerConstants[p.$type].findBackgroundColor(p.theme)};
    color: ${bannerConstants[p.$type].findIconColor(p.theme)};
    position: relative;

    grid-column-gap: 9px;
    align-items: flex-start;
    ${addGridStyles(p)};
    ${addPaddingStyles(p)};
    ${addBorderRadiusStyles(p)}
    ${p.$hasCloseInSmall &&
    css`
      padding-right: 36px;
    `}
  `}
`;

function addGridStyles(props: BannerStyleProps) {
  if (props.$hasImage) {
    return css`
      display: grid;
      grid-template-columns: auto 1fr 40%;
      grid-template-areas:
        'icon title image'
        '. content image'
        'footer footer image';
    `;
  }

  if (props.$hasInlineActionsWithClose) {
    return css`
      display: flex;
      align-items: center;
      flex-flow: row;
      flex-wrap: wrap;
    `;
  }

  if (props.$hasInlineActions) {
    return css`
      display: grid;
      align-items: center;
      grid-template-columns: auto 1fr auto;
      grid-template-areas:
        'icon title actions'
        '. content .';
    `;
  }

  return css`
    display: grid;
    grid-template-columns: auto 1fr max-content;
    grid-template-areas:
      'icon title title'
      '. content .'
      'footer footer .';
  `;
}

function addBorderRadiusStyles(props: BannerStyleProps) {
  if (props.$hasSmallBorderRadius) {
    return css`
      border-radius: ${bannerBorderRadii.small};
    `;
  }

  return css`
    border-radius: ${bannerBorderRadii.medium};
  `;
}

function addPaddingStyles(props: BannerStyleProps) {
  if (props.$size === VisualSizesEnum.MEDIUM || props.$size === VisualSizesEnum.LARGE) {
    return css`
      padding: 20px;
    `;
  }

  if (props.$hasInlineActions) {
    return css`
      padding: 6px 12px;
    `;
  }

  return css`
    padding: 9px 12px;
  `;
}

const StyledImageWrapperDiv = styled.div`
  grid-area: image;
  margin: -20px;

  img {
    display: block;
    max-width: 100%;
  }
`;

interface BannerTitleStyleProps
  extends BannerInsideCardStyleProps,
    BannerSizeStyleProps,
    BannerInlineActionStyleProps {
  $isBold?: boolean;
}
const StyledTitleDiv = styled.div<BannerTitleStyleProps>`
  ${(p) => css`
    grid-area: title;
    color: ${p.$hasColorfulText ? undefined : p.theme.greys.shade80};
    font-weight: ${p.$isBold ? fontWeights.bold : fontWeights.normal};
    line-height: 20px;
    font-size: ${bannerFontSizes[p.$size]};
  `}

  ${(p) =>
    p.$hasInlineActionsWithClose &&
    css`
      flex: 1;
      min-width: 65%;
    `}
`;

interface BannerContentProps extends BannerInsideCardStyleProps, BannerSizeStyleProps {}
const StyledContentDiv = styled.div<BannerContentProps>`
  ${(p) => css`
    grid-area: content;
    line-height: ${bannerLineHeights[p.$size]};
    color: ${p.$hasColorfulText ? undefined : p.theme.greys.shade80};
    font-size: ${bannerFontSizes[p.$size]};
    margin-top: 8px;

    ul,
    ol {
      padding: 0;
      margin-top: 0;
      margin-bottom: 0;
    }
  `}
`;

const StyledIconDiv = styled.div<BannerIconStyleProps>`
  grid-area: icon;
  color: ${(p) => bannerConstants[p.$type].findIconColor(p.theme)};
  margin-top: 2px;
`;

const footerBottomMargins = makeSizeConstants('-9px', '-20px');
const StyledFooterDiv = styled.div<BannerSizeStyleProps>`
  grid-area: footer;
  padding: 0 22px;
  margin-top: 7px;

  min-width: 0;
  display: flex;
  padding-bottom: 5px;
  padding-left: 17px;
  margin-bottom: ${(p) => footerBottomMargins[p.$size]};
`;

const StyledInlineActionsDiv = styled.div<BannerInlineActionStyleProps>`
  display: flex;

  ${(p) =>
    p.$hasInlineActionsWithClose
      ? css`
          align-items: center;
          text-transform: uppercase;
          padding-left: 26px;
        `
      : css`
          grid-area: actions;
        `}
`;

const StyledActionButton = styled(ChromelessButton)<BannerInsideCardStyleProps>`
  position: relative;

  padding: 0 8px;
  border-radius: 13px;

  color: ${(p) => p.theme.interactiveText};
  font-size: ${(p) => (p.$isNewDesign ? fontSizes.small : fontSizes.tiny)};
  font-weight: ${(p) => (p.$isNewDesign ? fontWeights.semibold : fontWeights.bold)};
  line-height: 26px;
  text-transform: ${(p) => (p.$isNewDesign ? 'none' : 'uppercase')};
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 4px;
  align-items: center;

  ${ellipsis()};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledCloseButton = styled(IconButton)`
  position: absolute;
  top: 5px;
  right: 5px;

  &:hover svg {
    color: ${(p) => p.theme.greys.shade80};
  }
`;

/*
 * Component.
 */

export const InlineBanner: FC<PropsWithChildren<InlineBannerProps>> = (props) => {
  const theme = useUserTheme();
  const hasMoreThanTitle = Boolean(props.title && props.children);
  const actualSize = props.size;
  const hasInlineActions = Boolean(props.actions && props.hasInlineActions);
  const hasInlineActionsWithClose = hasInlineActions && Boolean(props.onClose);

  return (
    <StyledBannerDiv
      $size={actualSize}
      $type={props.type}
      $hasContent={hasMoreThanTitle}
      $hasColorfulText={Boolean(props.hasColorfulText)}
      $hasSmallBorderRadius={Boolean(props.hasSmallBorderRadius)}
      $hasInlineActions={hasInlineActions}
      $hasInlineActionsWithClose={hasInlineActionsWithClose}
      $hasImage={Boolean(props.imageUrl)}
      $hasCloseInSmall={props.onClose && actualSize === VisualSizesEnum.SMALL}
      className={props.className}
    >
      {props.onClose && (
        <StyledCloseButton
          iconName="closeSmall"
          label={props.closeLabel || 'Close'}
          onClick={props.onClose}
          renderTooltip={() => null}
          hoverColor={theme.greys.shade80}
          color={theme.greys.shade70}
        />
      )}
      <StyledIconDiv $type={props.type}>
        <Icon name={bannerConstants[props.type].icon} />
      </StyledIconDiv>
      <StyledTitleDiv
        $size={actualSize}
        $hasColorfulText={Boolean(props.hasColorfulText)}
        $hasSmallBorderRadius={Boolean(props.hasSmallBorderRadius)}
        $isBold={hasMoreThanTitle}
        $hasInlineActionsWithClose={hasInlineActionsWithClose}
      >
        {props.title}
      </StyledTitleDiv>
      {props.hasInlineActions && props.actions && (
        <StyledInlineActionsDiv $hasInlineActionsWithClose={hasInlineActionsWithClose}>
          {props.actions.map((action) => (
            <StyledActionButton key={action.id} onClick={action.onClick}>
              {action.title}
              {action.iconName && <Icon name={action.iconName} size={16} />}
            </StyledActionButton>
          ))}
        </StyledInlineActionsDiv>
      )}
      {props.children && (
        <StyledContentDiv
          $size={actualSize}
          $hasColorfulText={Boolean(props.hasColorfulText)}
          $hasSmallBorderRadius={Boolean(props.hasSmallBorderRadius)}
        >
          {props.children}
        </StyledContentDiv>
      )}
      {props.imageUrl && (
        <StyledImageWrapperDiv>
          <img src={props.imageUrl} alt="" />
        </StyledImageWrapperDiv>
      )}
      {!props.hasInlineActions && props.actions && (
        <StyledFooterDiv $size={actualSize}>
          {props.actions.map((action) => (
            <StyledActionButton
              key={action.id}
              onClick={action.onClick}
              $hasColorfulText={Boolean(props.hasColorfulText)}
              $hasSmallBorderRadius={Boolean(props.hasSmallBorderRadius)}
              $isNewDesign={Boolean(props.isNewDesign)}
            >
              {action.title}
              {action.iconName && <Icon name={action.iconName} size={16} />}
            </StyledActionButton>
          ))}
        </StyledFooterDiv>
      )}
    </StyledBannerDiv>
  );
};
